/**
 * LabelReprintsTab — Label reprints tab for the SupervisorOverrideModal.
 * Shows active boxes with reprint/void actions and collapsible voided section.
 * Extracted from SupervisorOverrideModal.tsx for maintainability.
 */
import React, { useState, useRef, useEffect } from 'react';
import { Printer, Trash2, Info, X, ChevronDown, Loader2 } from 'lucide-react';
import { useLanguage } from '../../../i18n/LanguageContext';
import { formatGS1Barcode } from '../../../utils/barcodeUtils';
import { formatTime } from '../../../utils/dateUtils';
import JsBarcode from 'jsbarcode';

/** Inline barcode renderer using JsBarcode */
const BarcodeDisplay: React.FC<{ value: string }> = ({ value }) => {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (svgRef.current && value) {
      try {
        JsBarcode(svgRef.current, value, {
          format: 'CODE128',
          width: 1,
          height: 40,
          fontSize: 10,
          margin: 2,
          displayValue: false,
        });
      } catch (err) {
        console.error('Failed to render barcode:', err);
      }
    }
  }, [value]);

  return <svg ref={svgRef} />;
};

interface Box {
  id: string;
  box_number: string;
  original_box_number?: string;
  actual_weight: number;
  box_status: string;
  packed_at: string;
  voided_at?: string;
  label_printed_at?: string;
  packed_by_name: string;
  total_prints: number;
  barcode_number: string;
}

interface LabelReprintsTabProps {
  boxes: Box[];
  discardedLabels: Box[];
  loadingBoxes: boolean;
  reasons: { value: string; label: string }[];
  onReprint: (box: Box) => void;
  onVoidLabel: (boxId: string) => void;
  reprintingBoxId: string | null;
  voidingBoxId: string | null;
  reprintReason: string;
  onReprintReasonChange: (reason: string) => void;
  selectedBoxId: string | null;
  onSelectedBoxIdChange: (id: string | null) => void;
}

const LabelReprintsTab: React.FC<LabelReprintsTabProps> = ({
  boxes,
  discardedLabels,
  loadingBoxes,
  reasons,
  onReprint,
  onVoidLabel,
  reprintingBoxId,
  voidingBoxId,
  reprintReason,
  onReprintReasonChange,
  selectedBoxId,
  onSelectedBoxIdChange,
}) => {
  const { t } = useLanguage();
  const [showBarcodeInfoId, setShowBarcodeInfoId] = useState<string | null>(null);
  const [confirmVoidBoxId, setConfirmVoidBoxId] = useState<string | null>(null);
  const [voidedExpanded, setVoidedExpanded] = useState(false);

  return (
    <div className="space-y-3">
      {loadingBoxes ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          {t('common.loading') || 'Loading...'}
        </div>
      ) : boxes.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 text-center">
          <div className="w-16 h-16 mx-auto mb-3 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
            <Printer className="w-8 h-8 text-gray-400" />
          </div>
          <p className="text-gray-500 dark:text-gray-400">
            {t('sessionDetails.noBoxes')}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Active boxes (packed, pending) - exclude voided and cancelled */}
          {boxes.filter(b => b.box_status !== 'voided' && b.box_status !== 'cancelled').map((box) => (
            <div
              key={box.id}
              className="bg-white dark:bg-gray-800 p-3 rounded-xl"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold text-gray-900 dark:text-white">
                      #{box.box_number || '-'}
                    </span>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      box.box_status === 'packed' && box.total_prints >= 1
                        ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                        : box.box_status === 'packed'
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}>
                      {box.box_status === 'packed' && box.total_prints >= 1 ? 'reprinted' : box.box_status}
                    </span>
                    {box.total_prints > 2 && (
                      <span className="px-2 py-0.5 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full">
                        {box.total_prints - 1}x
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {Number(box.actual_weight).toFixed(2)} lbs • {formatTime(box.label_printed_at || box.packed_at)}
                  </p>
                </div>
                {selectedBoxId !== box.id && confirmVoidBoxId !== box.id && (
                  <div className="flex items-center gap-2">
                    {box.box_status === 'pending' && (
                      <button
                        onClick={() => setConfirmVoidBoxId(box.id)}
                        className="p-2 text-sm bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg font-medium active:scale-95 transition-transform"
                        title={t('supervisorOverride.voidLabel')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => setShowBarcodeInfoId(showBarcodeInfoId === box.id ? null : box.id)}
                      className="p-2 text-sm bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg font-medium active:scale-95 transition-transform"
                      title={t('supervisorOverride.viewBarcode')}
                    >
                      <Info className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onSelectedBoxIdChange(box.id)}
                      className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg font-medium active:scale-95 transition-transform"
                    >
                      <Printer className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {/* Confirm Void */}
                {confirmVoidBoxId === box.id && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setConfirmVoidBoxId(null)}
                      className="px-3 py-2 text-sm bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg font-medium active:scale-95 transition-transform"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      onClick={() => onVoidLabel(box.id)}
                      disabled={voidingBoxId === box.id}
                      className="px-3 py-2 text-sm bg-red-600 text-white rounded-lg font-medium active:scale-95 transition-transform disabled:opacity-50"
                    >
                      {voidingBoxId === box.id ? '...' : t('supervisorOverride.confirmVoid')}
                    </button>
                  </div>
                )}
              </div>

              {/* Barcode Info Overlay */}
              {showBarcodeInfoId === box.id && (
                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                  <div className="bg-white dark:bg-gray-700 rounded-lg p-4 flex flex-col items-center">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      {t('supervisorOverride.barcodeInfo')}
                    </p>
                    {box.barcode_number ? (
                      <>
                        <div className="bg-white p-2 rounded overflow-hidden max-w-full">
                          <BarcodeDisplay value={box.barcode_number} />
                        </div>
                        <p className="mt-2 font-mono text-xs text-gray-900 dark:text-white select-all text-center leading-relaxed break-all px-2">
                          {formatGS1Barcode(box.barcode_number)}
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-400 dark:text-gray-500 text-sm">
                        {t('supervisorOverride.noBarcodeAvailable')}
                      </p>
                    )}
                    <button
                      onClick={() => setShowBarcodeInfoId(null)}
                      className="mt-3 px-4 py-1.5 text-xs bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-lg"
                    >
                      {t('common.close')}
                    </button>
                  </div>
                </div>
              )}

              {selectedBoxId === box.id && (
                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 space-y-2">
                  <select
                    value={reprintReason}
                    onChange={(e) => onReprintReasonChange(e.target.value)}
                    className="w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2.5 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">{t('supervisorOverride.selectReason')}</option>
                    {reasons.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                  <div className="flex gap-2">
                    <button
                      onClick={() => onReprint(box)}
                      disabled={!reprintReason || reprintingBoxId === box.id}
                      className="flex-1 px-4 py-2.5 text-sm bg-blue-600 text-white rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {reprintingBoxId === box.id ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          {t('supervisorOverride.reprinting')}
                        </>
                      ) : t('supervisorOverride.reprintButton')}
                    </button>
                    <button
                      onClick={() => { onSelectedBoxIdChange(null); onReprintReasonChange(''); }}
                      className="px-4 py-2.5 text-sm bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-lg"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Voided/Cancelled boxes and discarded labels - collapsible section */}
          {(() => {
            const voidedCancelledBoxes = boxes.filter(b => b.box_status === 'voided' || b.box_status === 'cancelled');
            const totalVoided = voidedCancelledBoxes.length + discardedLabels.length;
            if (totalVoided === 0) return null;

            type VoidedItem = { type: 'box' | 'label'; data: Box; time: string };
            const allVoidedItems: VoidedItem[] = [
              ...voidedCancelledBoxes.map(b => ({ type: 'box' as const, data: b, time: b.voided_at || b.packed_at })),
              ...discardedLabels.map(l => ({ type: 'label' as const, data: l, time: l.voided_at || l.packed_at }))
            ].sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());

            const lastVoidedTime = allVoidedItems[0]?.time
              ? formatTime(allVoidedItems[0].time)
              : '';

            return (
              <div className="mt-4">
                <button
                  onClick={() => setVoidedExpanded(!voidedExpanded)}
                  className="w-full bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 rounded-xl"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                        voided ({totalVoided})
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">{lastVoidedTime}</span>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${voidedExpanded ? 'rotate-180' : ''}`} />
                    </div>
                  </div>
                </button>

                {voidedExpanded && (
                  <div className="mt-2 space-y-2 pl-4 border-l-2 border-red-200 dark:border-red-800">
                    {allVoidedItems.map((item) => item.type === 'box' ? (
                      <div
                        key={`box-${item.data.id}`}
                        className="p-2 rounded-lg bg-red-50 dark:bg-red-900/10"
                      >
                        <div className="flex items-center justify-between gap-1.5">
                          <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                            <span className="font-mono font-bold text-[12px] text-gray-500 dark:text-gray-400">
                              #{item.data.original_box_number || '-'}
                            </span>
                            <span className="text-[12px] text-gray-600 dark:text-gray-400">
                              {Number(item.data.actual_weight).toFixed(2)} lbs
                            </span>
                            <span className="px-1.5 py-0.5 text-[12px] rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                              voided
                            </span>
                            <span className="text-[12px] text-gray-400">
                              {formatTime(item.data.voided_at || item.data.packed_at)}
                            </span>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowBarcodeInfoId(showBarcodeInfoId === item.data.id ? null : item.data.id);
                            }}
                            className="p-1.5 text-sm bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg active:scale-95 transition-transform flex-shrink-0"
                            title={t('supervisorOverride.viewBarcode')}
                          >
                            <Info className="w-3.5 h-3.5" />
                          </button>
                        </div>

                        {showBarcodeInfoId === item.data.id && (
                          <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                            <div className="bg-white dark:bg-gray-700 rounded-lg p-3 flex flex-col items-center">
                              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                {t('supervisorOverride.barcodeInfo')}
                              </p>
                              {item.data.barcode_number ? (
                                <>
                                  <div className="bg-white p-2 rounded overflow-hidden max-w-full">
                                    <BarcodeDisplay value={item.data.barcode_number} />
                                  </div>
                                  <p className="mt-2 font-mono text-xs text-gray-900 dark:text-white select-all text-center leading-relaxed break-all px-2">
                                    {formatGS1Barcode(item.data.barcode_number)}
                                  </p>
                                </>
                              ) : (
                                <p className="text-gray-400 dark:text-gray-500 text-sm">
                                  {t('supervisorOverride.noBarcodeAvailable')}
                                </p>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowBarcodeInfoId(null);
                                }}
                                className="mt-2 px-3 py-1 text-xs bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-lg"
                              >
                                {t('common.close')}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      /* Discarded label */
                      <div
                        key={`label-${item.data.id}`}
                        className="p-2 rounded-lg bg-amber-50 dark:bg-amber-900/10"
                      >
                        <div className="flex items-center justify-between gap-1.5">
                          <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                            <span className="font-mono font-bold text-[12px] text-gray-500 dark:text-gray-400">
                              #{item.data.original_box_number || '-'}
                            </span>
                            <span className="text-[12px] text-gray-600 dark:text-gray-400">
                              {Number(item.data.actual_weight).toFixed(2)} lbs
                            </span>
                            <span className="px-1.5 py-0.5 text-[12px] rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                              discarded
                            </span>
                            <span className="text-[12px] text-gray-400">
                              {formatTime(item.data.voided_at || item.data.packed_at)}
                            </span>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowBarcodeInfoId(showBarcodeInfoId === item.data.id ? null : item.data.id);
                            }}
                            className="p-1.5 text-sm bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg active:scale-95 transition-transform flex-shrink-0"
                            title={t('supervisorOverride.viewBarcode')}
                          >
                            <Info className="w-3.5 h-3.5" />
                          </button>
                        </div>

                        {showBarcodeInfoId === item.data.id && (
                          <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                            <div className="bg-white dark:bg-gray-700 rounded-lg p-3 flex flex-col items-center">
                              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                {t('supervisorOverride.barcodeInfo')}
                              </p>
                              {item.data.barcode_number ? (
                                <>
                                  <div className="bg-white p-2 rounded overflow-hidden max-w-full">
                                    <BarcodeDisplay value={item.data.barcode_number} />
                                  </div>
                                  <p className="mt-2 font-mono text-xs text-gray-900 dark:text-white select-all text-center leading-relaxed break-all px-2">
                                    {formatGS1Barcode(item.data.barcode_number)}
                                  </p>
                                </>
                              ) : (
                                <p className="text-gray-400 dark:text-gray-500 text-sm">
                                  {t('supervisorOverride.noBarcodeAvailable')}
                                </p>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowBarcodeInfoId(null);
                                }}
                                className="mt-2 px-3 py-1 text-xs bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-lg"
                              >
                                {t('common.close')}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default LabelReprintsTab;
